<script lang="ts" setup>
  import { Typo, Icon, Spinner } from '@travelata-libs/ui-kit';
  import {
    parseDate,
    formatDateWithTimezone,
    getCurrentTimeZone,
    addDaysToDate,
    pluralize,
  } from '@travelata-libs/utils';
  import { computed } from 'vue';
  import { type ApiTour } from '@/entities/tour';
  import { type AccountUser } from '@/features/account';
  import { SiteHeader } from '@/widgets/siteHeader';

  interface HeaderProps {
    user?: AccountUser | null;
    accountUrl?: string;
    tour: ApiTour | null;
  }

  interface HeaderEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
  }

  const emit = defineEmits<HeaderEmits>();

  const props = defineProps<HeaderProps>();

  const formatDate = (date: Date) => {
    return formatDateWithTimezone(date, getCurrentTimeZone(), 'd MMMM');
  };

  const touristsText = computed(() => {
    const touristsGroup = props.tour?.touristGroup;
    if (!touristsGroup) return '';
    const { adults, infants, kids } = touristsGroup;
    const roomsCount = props.tour?.roomGroups.length || 1;
    const touristsCount = adults + infants + kids;
    return `${pluralize(['номер', 'номера', 'номеров'], roomsCount)} для ${pluralize(
      ['туриста', 'туристов', 'туристов'],
      touristsCount,
    )}`;
  });

  const checkInDateText = computed(() => {
    const dateRange = parseDate(props.tour?.checkInDate || '');
    if (!dateRange) return '';
    const dateTo = addDaysToDate(dateRange, props.tour?.nights.tour || 0);
    return `${formatDate(dateRange)} — ${formatDate(dateTo)}`;
  });
</script>

<template>
  <header class="tour-page-header">
    <SiteHeader
      :user="user"
      :account-url="accountUrl"
      class="tour-page-header__site"
      @login="emit('login')"
      @logout="emit('logout')"
    >
      <template #nav>
        <div class="tour-page-header__container">
          <div
            v-if="!tour"
            class="tour-page-header__loader"
          >
            <Spinner
              size="xs"
              color="white"
            />
            <Typo>Загружаем информацию о туре</Typo>
          </div>
          <template v-else>
            <Typo>{{ tour.departureCity.name }}</Typo>
            <Icon
              class="tour-page-header__icon"
              name="chevron-right"
              size="m"
            />
            <Typo>{{ tour.country.name }}, {{ tour.resort.name }}</Typo>
            <Icon
              class="tour-page-header__icon"
              name="point-filled"
              size="xxs"
            />
            <Typo>{{ checkInDateText }}</Typo>
            <Icon
              class="tour-page-header__icon"
              name="point-filled"
              size="xxs"
            />
            <Typo>{{ touristsText }}</Typo>
          </template>
        </div>
      </template>
    </SiteHeader>
  </header>
</template>

<style lang="scss" scoped>
  .tour-page-header {
    color: var(--color-text-inverse);

    p {
      text-box: trim-both cap alphabetic;
    }

    &__site {
      padding-bottom: var(--spacing-s);
    }

    &__loader {
      align-items: center;
      display: flex;
      gap: var(--spacing-s);
    }

    &__container {
      align-items: center;
      display: flex;
      gap: var(--spacing-xxs);
    }

    &__icon {
      fill: var(--color-text-inverse);
    }
  }
</style>
